/**
* Composition for Addresses logic
*/
export default function (optionsGlobal = { key: 'short' }) {
  const { confGlobal } = useConfig()
  const { $i18n: i18n } = useNuxtApp()
  const { query, path } = useRoute()
  const { push: routerPush } = useRouter()
  const { requestHeaders } = useApi()
  const toast = useToast()

  /**
  * Default Limit
  */
  const defaultLimit = 12

  /**
  * Get Addresses List
  */
  const addresses = useState('customer_addresses_short', () => false)
  const addressesFull = useState('customer_addresses_full', () => false)
  const getAddresses = (paramsData = {}, options = {}) => {
    try {
      if (optionsGlobal.key === 'short') {
        addresses.value = 'loading'
      } else { 
        addressesFull.value = 'loading'
      }

      // Get Parameters
      const params = {}

      if (paramsData.page) {
        params.page = paramsData.page
      } else {
        params.page = parseInt(query.page) > 0 ? query.page * 1 : 1
      }

      if (paramsData.limit) {
        params.limit = paramsData.limit
      } else {
        params.limit = defaultLimit
      }

      params['filters[skip_incomplete]'] = true

      let paramsString = new URLSearchParams(params).toString()
      if (paramsString) {
        paramsString = '?' + paramsString
      }

      return $fetch(confGlobal.apiUrl + `/addresses${paramsString}`, {
        method: 'GET',
        headers: requestHeaders()
      }).then(async (res) => {
        const addressesData = await res

        // Add Full Address Str
        const newAddressesArr = []
        for (const address of addressesData.data.addresses) {
          let fullAddressStr = ''
          if (address.firstname || address.lastname) {
            fullAddressStr = address.firstname
            if (address.firstname && address.lastname) {
              fullAddressStr += ' ' + address.lastname
            }
          }
          if (address.company) {
            fullAddressStr += fullAddressStr ? ', ' + address.company.trim() : address.company.trim()
          }

          if (address.address_1) {
            fullAddressStr += fullAddressStr ? ', ' + address.address_1.trim() : address.address_1.trim()
          }

          if (address.address_2) {
            fullAddressStr += fullAddressStr ? ', ' + address.address_2.trim() : address.address_2.trim()
          }

          if (address.city) {
            fullAddressStr += fullAddressStr ? ', ' + address.city.trim() : address.city.trim()
          }

          if (address.postcode) {
            fullAddressStr += fullAddressStr ? ', ' + address.postcode.trim() : address.postcode.trim()
          }

          if (address.zone) {
            fullAddressStr += fullAddressStr ? ', ' + address.zone.trim() : address.zone.trim()
          }

          if (address.country) {
            fullAddressStr += fullAddressStr ? ', ' + address.country.trim() : address.country.trim()
          }

          address.fullAddress = fullAddressStr

          newAddressesArr.push(address)
        }

        if (optionsGlobal.key === 'short') {
          addresses.value = addressesData
          addresses.value.data.addresses = newAddressesArr
        } else { 
          addressesFull.value = addressesData
          addressesFull.value.data.addresses = newAddressesArr
        }

        // Update URL
        if (!options.fullList && !options.simple) {
          const additingParams = {}

          if (params.page > 1) {
            additingParams.page = params.page
          }

          if (Object.keys(additingParams).length > 0) {
            routerPush(path + '?' + new URLSearchParams(additingParams).toString())
          } else {
            routerPush(path)
          }
        }
      }).catch((err) => {
        console.log(err)

        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Add New Addresses
  */
  const addAddress = (formData) => {
    try {
      return $fetch(confGlobal.apiUrl + '/addresses', {
        method: 'POST',
        body: formData,
        headers: requestHeaders()
      }).then(async (res) => {
        toast.add({
          severity: 'success',
          detail: i18n?.t('addressesSuccessAdded'),
          life: 5000
        })

        // Reset Addresses
        addresses.value = false
        addressesFull.value = false

        return await res
      }).catch((err) => {
        console.log(err)

        if (err?.data?.error?.description) {
          return {
            status: 'error',
            code: err?.data?.error?.code,
            description: err?.data?.error?.description
          }
        } else {
          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
          return i18n?.t('serverError')
        }
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Edit Address
  */
  const editAddress = (formData, id) => {
    try {
      return $fetch(confGlobal.apiUrl + '/addresses/' + id, {
        method: 'PUT',
        body: formData,
        headers: requestHeaders()
      }).then((res) => {
        toast.add({
          severity: 'success',
          detail: i18n?.t('addressSuccessfullyEdited'),
          life: 5000
        })

        // Reset Addresses
        addresses.value = false
        addressesFull.value = false

        return res
      }).catch((err) => {
        console.log(err)

        if (err?.data?.error?.description) {
          return {
            status: 'error',
            code: err?.data?.error?.code,
            description: err?.data?.error?.description
          }
        } else {
          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
          return i18n?.t('serverError')
        }
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Delete Addresses
  */
  const deleteAddress = (formData) => {
    try {
      return $fetch(confGlobal.apiUrl + '/addresses/' + formData.id, {
        method: 'DELETE',
        headers: requestHeaders()
      }).then(() => {
        toast.add({
          severity: 'success',
          detail: i18n?.t('addressSuccessfullyDeleted'),
          life: 5000
        })

        // Reset Addresses
        addresses.value = false
        addressesFull.value = false

        return true
      }).catch((err) => {
        console.log(err)

        toast.add({
          severity: 'error',
          detail: i18n?.t('serverError'),
          life: 5000
        })
        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Address Downloads
  */
  const getAddressDownloads = (type) => {
    try {
      return $fetch(confGlobal.apiUrl + '/addresses/files/' + type, {
        method: 'GET',
        headers: requestHeaders(),
        responseType: 'blob'
      }).then(async (res) => {
        return await res
      }).catch((err) => {
        console.log(err)

        toast.add({
          severity: 'error',
          detail: i18n?.t('serverError'),
          life: 5000
        })
        return true
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Upload Addresses
  */
  const uploadAddresses = async (formData) => {
    try {
      return await $fetch(confGlobal.apiUrl + '/addresses/files/upload', {
        method: 'POST',
        body: formData,
        headers: requestHeaders({
          'content-type': false
        })
      }).then((res) => {
        return { success: true, data: res.data }
      }).catch((err) => {
        console.log(err)

        if (err?.data?.error?.code === 422) {
          toast.add({
            severity: 'error',
            detail: i18n?.t('invalidXLSXFileReceived'),
            life: 5000
          })
        } else {
          toast.add({
            severity: 'error',
            detail: i18n?.t('serverError'),
            life: 5000
          })
        }
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
  * Return Object
  */
  return {
    defaultLimit,

    addresses,
    addressesFull,
    getAddresses,
    addAddress,
    editAddress,
    deleteAddress,

    getAddressDownloads,
    uploadAddresses
  }
}
